import React, { Component } from 'react'

class Viewer extends Component {
  constructor(props) {
    super(props)
    this.canvas = React.createRef()
    this.canvasZoom = React.createRef()
    this.canvasContainer = React.createRef()
    this.loadedImages = []
    this.xInitial = null
    this.xLast = null
    this.currentImage = 0
    this.animation = null
    this.observer = null
    this.scrollCache = 0
    this.trackScroll = true
  }

  componentDidMount() {

    if (this.props.scrollController) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && this.loadedImages.length === 0) {
            this.preloadAndListen(true)
          }
        },
        {
          rootMargin: '2000px',
          threshold: 0
        }
      )
      if (this.canvas.current) {
        this.observer.observe(this.canvas.current)
      }
    } else {
      this.preloadAndListen()
    }
  }

  preloadAndListen() {
    if (this.props.icon) {
      const canvasContainer = this.canvasContainer.current
      this.loadImages().then(() => {
      canvasContainer.addEventListener('mousedown', this.handleMouseDown, false)
      canvasContainer.addEventListener('touchstart', this.handleTouchStart, false)
      canvasContainer.addEventListener('mousemove', this.handleMouseMove, false)
      canvasContainer.addEventListener('touchmove', this.handleTouchMove, false)
      canvasContainer.addEventListener('mouseup', this.handleMouseUp, false)
      canvasContainer.addEventListener('mouseleave', this.handleMouseUp, false)
      canvasContainer.addEventListener('touchend', this.handleMouseUp, false)
      if (this.props.scrollController) {
        window.addEventListener(
          'scroll',
          this.throttle(this.handleScroll.bind(this), 80, true)
        )
      }
      this.drawImage(0)
    })
    } else {
      // Pre-load all product images and then add the event listeners to the canvas and draw the first product image.
      const canvas = this.canvas.current
      this.loadImages().then(() => {
        canvas.addEventListener('mousedown', this.handleMouseDown, false)
        canvas.addEventListener('touchstart', this.handleTouchStart, false)
        canvas.addEventListener('mousemove', this.handleMouseMove, false)
        canvas.addEventListener('touchmove', this.handleTouchMove, false)
        canvas.addEventListener('mouseup', this.handleMouseUp, false)
        canvas.addEventListener('mouseleave', this.handleMouseUp, false)
        canvas.addEventListener('touchend', this.handleMouseUp, false)
        if (this.props.scrollController) {
          window.addEventListener(
            'scroll',
            this.throttle(this.handleScroll.bind(this), 80, true)
          )
        }
        this.drawImage(0)
      })
    }
  }

  throttle(func, wait, options) {
    var context, args, result
    var timeout = null
    var previous = 0
    if (!options) options = {}
    var later = function() {
      previous = options.leading === false ? 0 : Date.now()
      timeout = null
      result = func.apply(context, args)
      if (!timeout) context = args = null
    }
    return function() {
      var now = Date.now()
      if (!previous && options.leading === false) previous = now
      var remaining = wait - (now - previous)
      context = this
      args = arguments
      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout)
          timeout = null
        }
        previous = now
        result = func.apply(context, args)
        if (!timeout) context = args = null
      } else if (!timeout && options.trailing !== false) {
        timeout = setTimeout(later, remaining)
      }
      return result
    }
  }

  componentWillUnmount() {
    if (this.props.icon) {
      const canvasContainer = this.canvasContainer.current
      cancelAnimationFrame(this.animation)
      this.trackScroll = false

      // Remove all event listeners before the component gets removed from the page.
      canvasContainer.removeEventListener('mousedown', this.handleMouseDown, false)
      canvasContainer.removeEventListener('touchstart', this.handleTouchStart, false)
      canvasContainer.removeEventListener('mousemove', this.handleMouseMove, false)
      canvasContainer.removeEventListener('touchmove', this.handleTouchMove, false)
      canvasContainer.removeEventListener('mouseup', this.handleMouseUp, false)
      canvasContainer.removeEventListener('touchend', this.handleMouseUp, false)
      canvasContainer.removeEventListener('mouseleave', this.handleMouseUp, false)

      if (this.props.scrollController) {
        window.addEventListener(
          'scroll',
          this.throttle(this.handleScroll.bind(this), 0, true)
        )
      }
    } else {
      const canvas = this.canvas.current

      cancelAnimationFrame(this.animation)
      this.trackScroll = false

      // Remove all event listeners before the component gets removed from the page.
      canvas.removeEventListener('mousedown', this.handleMouseDown, false)
      canvas.removeEventListener('touchstart', this.handleTouchStart, false)
      canvas.removeEventListener('mousemove', this.handleMouseMove, false)
      canvas.removeEventListener('touchmove', this.handleTouchMove, false)
      canvas.removeEventListener('mouseup', this.handleMouseUp, false)
      canvas.removeEventListener('touchend', this.handleMouseUp, false)
      canvas.removeEventListener('mouseleave', this.handleMouseUp, false)

      if (this.props.scrollController) {
        window.addEventListener(
          'scroll',
          this.throttle(this.handleScroll.bind(this), 0, true)
        )
      }
    }
  }

  handleScroll() {
    if (this.canvas.current) {
      const { top } = this.canvas.current.getBoundingClientRect()
      if (top < window.innerHeight && top > -window.innerHeight) {
        this.handleScrollMove({ pageX: top })
      }
    }
  }

  loadImages() {
    const { images } = this.props
    return new Promise(resolve => {
      const imagesSorted = images.sort(function (a, b) {
        if (a.file && b.file) {
          var nameA = a.file.toUpperCase()
          var nameB = b.file.toUpperCase()
          if (nameA < nameB) {
            return -1
          }
          if (nameA > nameB) {
            return 1
          }
          return 0
        }
      })
      imagesSorted.forEach(({ file: image }) => {
        const img = new Image()
        img.src = image
        img.addEventListener(
          'load',
          () => {
            // Add the loaded image to the array to be used later.
            this.loadedImages.push(img)
            // Resolve the promise, if all the images have been loaded. Otherwise, draw the loading bar to show the progress.
            if (this.loadedImages.length === images.length) {
              if (this.props.setLoadedCanvas) {
                this.props.setLoadedCanvas(true)
              }
              resolve()
            }
          },
          false
        )
      })
    })
  }

  drawLoadingBar(progress) {
    const canvas = this.canvas.current
    const context = canvas.getContext('2d')
    const barWidth = Math.round(window.innerWidth / 5)
    const barHeight = Math.round(barWidth / 20)
    const barPosX = (canvas.width - barWidth) / 2
    const barPosY = (canvas.height - barHeight) / 2

    // Draw the progress bar background.
    // context.fillStyle = '#1A1A1A'
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.fillRect(barPosX, barPosY, barWidth, barHeight)

    // Draw the progress bar fill.
    // context.fillStyle = '#ffffff'
    const fillVal = Math.min(Math.max(progress / 100, 0), 1)
    context.fillRect(
      barPosX + 1,
      barPosY + 1,
      fillVal * (barWidth - 2),
      barHeight - 2
    )
  }

  drawImage(frame) {
    const canvas = this.canvas.current
    if (this.props.parent.current) {
      const {
        width,
        height
      } = this.props.parent.current.getBoundingClientRect()
      this.width = width
      this.height = height
      canvas.width = this.width
      canvas.height = this.height
    }
    if (this.props.scrollController && canvas) {
      if (window.innerWidth <= 768) {
        const wRatio = (window.innerWidth * 0.8) / this.loadedImages[0].width
        canvas.width = this.loadedImages[0].width * wRatio
        canvas.height = this.loadedImages[0].height * wRatio
      } else {
        canvas.width = this.loadedImages[0].width
        canvas.height = this.loadedImages[0].height
      }
    }
    const context = canvas.getContext('2d')

    // Clear the canvas before starting to draw
    context.clearRect(0, 0, canvas.width, canvas.height)
    // Get the image element to draw on canvas from the pre-loaded images array.
    const newImage = this.loadedImages.filter(
      img => img.src.indexOf(this.props.images[frame].file) > -1
    )[0]
    if (this.props.setCurrentImage) {
      this.props.setCurrentImage(frame)
    }
    // Draw the image on canvas
    context.drawImage(newImage, 0, 0, canvas.width, canvas.height)
    this.currentImage = frame
  }

  handleMouseDown = event => {
    this.xInitial = event.pageX
  }

  handleTouchStart = event => {
    this.xInitial = event.touches[0].pageX
  }

  handleMouseMove = event => {
    if (this.scrolling) {
      return
    }
    if (this.xInitial !== null) {
      const delta = event.pageX - (!this.xLast ? this.xInitial : this.xLast)
      this.xLast = event.pageX

      let startingFrame = this.currentImage
      if (this.currentImage === this.loadedImages.length - 1) {
        startingFrame = 0
      } else if (this.currentImage === 0) {
        startingFrame = this.loadedImages.length - 1
      }

      let moveFrame = startingFrame
      if (delta > 0) {
        moveFrame = startingFrame - 1
      } else if (delta < 0) {
        moveFrame = startingFrame + 1
      }

      this.newFrame = Math.min(
        Math.max(moveFrame, 0),
        this.loadedImages.length - 1
      )

      if (this.animation === null) {
        this.animation = requestAnimationFrame(this.animationFrame)
      }
    }
  }

  handleScrollMove = event => {
    clearTimeout(this.scrollTiemout)
    this.scrolling = true
    this.scrollTimeout = setTimeout(() => {
      this.scrolling = false
    }, 300)
    const delta = event.pageX - (!this.xLast ? this.xInitial : this.xLast)

    this.xLast = event.pageX

    let startingFrame = this.currentImage
    if (this.currentImage === this.loadedImages.length - 1) {
      startingFrame = 0
    } else if (this.currentImage === 0) {
      startingFrame = this.loadedImages.length - 1
    }

    let moveFrame = startingFrame
    if (delta > 0) {
      moveFrame = startingFrame - 1
    } else if (delta < 0) {
      moveFrame = startingFrame + 1
    }

    this.newFrame = Math.min(
      Math.max(moveFrame, 0),
      this.loadedImages.length - 1
    )
    this.drawImage(this.newFrame)
  }

  animationFrame = () => {
    if (this.trackScroll) {
      this.drawImage(this.newFrame)
      this.animation = requestAnimationFrame(this.animationFrame)
    }
  }

  handleTouchMove = event => this.handleMouseMove(event.touches[0])

  handleMouseUp = () => {
    this.xInitial = null
    this.xLast = null
    this.animation && cancelAnimationFrame(this.animation)
    this.animation = null
  }

  render() {
    const icon = <svg width="50px" height="40px" viewBox="0 0 50 40" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
        <defs>
            <polygon id="path-1" points="1.250625 0.001125 25 0.001125 25 34.75175 1.250625 34.75175"></polygon>
        </defs>
        <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g id="Latex_800" transform="translate(-669.000000, -5813.000000)" fill="#1A1A1A">
                <g id="Group-19" transform="translate(0.000000, 4757.000000)">
                    <g id="Group-27">
                        <g id="360_icon" transform="translate(669.000000, 1056.000000)">
                            <g id="Group-14" fill="none">
                                <path d="M18.1175,23.3305 C17.975,23.4605 17.7825,23.5605 17.5425,23.6255 C17.3,23.693 17,23.7255 16.64,23.7255 C16.325,23.7255 15.99,23.708 15.6425,23.6755 C15.2925,23.643 14.995,23.603 14.7525,23.563 L14.7525,24.2005 C14.7525,24.283 14.7725,24.3555 14.81,24.4205 C14.8475,24.483 14.925,24.533 15.0425,24.5755 C15.15,24.618 15.29,24.653 15.4625,24.6805 C15.6325,24.7105 15.805,24.7355 15.985,24.7555 C16.165,24.778 16.335,24.7955 16.5,24.808 C16.66,24.8205 16.7975,24.8255 16.905,24.8255 C17.8475,24.8255 18.5625,24.6105 19.055,24.183 C19.545,23.753 19.7925,23.118 19.7925,22.2755 C19.7925,21.7755 19.6825,21.3505 19.4675,21.0005 C19.25,20.6505 18.8925,20.3905 18.39,20.2255 C19.21,19.8355 19.6175,19.1855 19.6175,18.2755 C19.6175,17.593 19.4075,17.0455 18.985,16.638 C18.565,16.2305 17.9075,16.0255 17.0175,16.0255 C16.5425,16.0255 16.1225,16.0655 15.7625,16.143 C15.3975,16.223 15.095,16.308 14.8525,16.4005 L14.8525,16.9505 C14.8525,17.0405 14.875,17.1155 14.9175,17.168 C14.9575,17.2255 15.0375,17.2505 15.1525,17.2505 C15.305,17.2505 15.5075,17.228 15.7675,17.1805 C16.025,17.1355 16.3425,17.113 16.715,17.113 C17.325,17.113 17.7525,17.2205 17.9975,17.433 C18.245,17.6455 18.3675,17.9705 18.3675,18.413 C18.3675,18.563 18.3475,18.7155 18.31,18.8755 C18.2725,19.033 18.2,19.1805 18.09,19.313 C17.9825,19.4455 17.8325,19.5555 17.6375,19.643 C17.44,19.733 17.1825,19.7755 16.8675,19.7755 L16.0675,19.7755 L16.0675,20.563 C16.0675,20.7555 16.1675,20.8505 16.3675,20.8505 L16.84,20.8505 C17.4075,20.8505 17.83,20.953 18.105,21.158 C18.3775,21.3605 18.515,21.7155 18.515,22.2255 C18.515,22.458 18.485,22.668 18.4225,22.858 C18.36,23.0455 18.26,23.203 18.1175,23.3305" id="Fill-1" fill="#131313"></path>
                                <path d="M23.4715,20.51325 C23.7415,20.43825 24.0165,20.40075 24.2915,20.40075 C24.564,20.40075 24.7915,20.44075 24.974,20.51825 C25.1515,20.59825 25.294,20.71075 25.399,20.85825 C25.5015,21.00075 25.574,21.17575 25.614,21.37575 C25.6565,21.57575 25.679,21.79825 25.679,22.03825 C25.679,22.62325 25.5715,23.06075 25.354,23.35075 C25.1365,23.64325 24.7765,23.78825 24.2765,23.78825 C23.979,23.78825 23.729,23.73075 23.5265,23.61825 C23.3265,23.50575 23.169,23.33075 23.0465,23.09325 C22.9265,22.85575 22.8415,22.55325 22.7965,22.18825 C22.7515,21.82075 22.729,21.38075 22.729,20.86325 C22.954,20.70325 23.2015,20.58825 23.4715,20.51325 M21.479,20.63825 C21.479,21.32825 21.524,21.93575 21.6165,22.45575 C21.7065,22.97575 21.8615,23.41325 22.0765,23.76325 C22.294,24.11075 22.5815,24.37825 22.934,24.55575 C23.289,24.73575 23.7315,24.82575 24.264,24.82575 C24.7415,24.82575 25.144,24.75825 25.479,24.62575 C25.8115,24.49325 26.084,24.30325 26.2965,24.05575 C26.509,23.81075 26.664,23.51575 26.759,23.16825 C26.8565,22.82325 26.904,22.44075 26.904,22.02325 C26.904,21.12575 26.7015,20.45825 26.2965,20.02075 C25.894,19.58075 25.3065,19.36325 24.5415,19.36325 C24.124,19.36325 23.7715,19.41575 23.484,19.52075 C23.199,19.62575 22.944,19.75075 22.729,19.90075 C22.7365,19.36825 22.784,18.92075 22.8665,18.56325 C22.949,18.20325 23.0715,17.91825 23.234,17.70075 C23.399,17.48325 23.6115,17.33075 23.8715,17.23575 C24.1365,17.14575 24.454,17.10075 24.829,17.10075 C25.094,17.10075 25.349,17.11075 25.5915,17.13075 C25.8315,17.15325 26.109,17.18325 26.4165,17.22575 L26.4165,16.71075 C26.4165,16.62075 26.4015,16.53825 26.3715,16.46325 C26.3415,16.38825 26.2615,16.32575 26.1265,16.27575 C25.9365,16.20075 25.6965,16.14325 25.4015,16.10075 C25.1115,16.06075 24.8115,16.03825 24.504,16.03825 C24.029,16.03825 23.604,16.11325 23.2265,16.26325 C22.854,16.41325 22.5365,16.66575 22.2765,17.02075 C22.019,17.37325 21.8215,17.84825 21.684,18.43825 C21.5465,19.02825 21.479,19.76325 21.479,20.63825" id="Fill-3" fill="#131313"></path>
                                <path d="M29.3895,18.858 C29.432,18.4355 29.5095,18.093 29.622,17.833 C29.7345,17.568 29.887,17.3805 30.082,17.268 C30.2795,17.1555 30.532,17.1005 30.8395,17.1005 C31.147,17.1005 31.402,17.1555 31.597,17.268 C31.792,17.3805 31.9445,17.5655 32.057,17.8255 C32.1695,18.083 32.2495,18.4255 32.2895,18.8505 C32.332,19.2755 32.3545,19.7955 32.3545,20.413 C32.3545,21.038 32.332,21.563 32.2895,21.988 C32.2495,22.413 32.1695,22.758 32.057,23.018 C31.9445,23.283 31.792,23.4705 31.597,23.583 C31.402,23.6955 31.147,23.7505 30.8395,23.7505 C30.532,23.7505 30.2795,23.6955 30.082,23.583 C29.887,23.4705 29.7345,23.283 29.622,23.018 C29.5095,22.758 29.432,22.413 29.3895,21.988 C29.347,21.563 29.3295,21.043 29.3295,20.4255 C29.3295,19.8005 29.347,19.278 29.3895,18.858 M29.6695,24.643 C30.017,24.7655 30.407,24.8255 30.8395,24.8255 C31.2745,24.8255 31.662,24.7655 32.0095,24.643 C32.3545,24.5255 32.6495,24.3005 32.8895,23.9755 C33.132,23.6505 33.3195,23.203 33.452,22.633 C33.587,22.0605 33.652,21.3255 33.652,20.4255 C33.652,19.5255 33.587,18.7905 33.452,18.218 C33.3195,17.6505 33.132,17.2005 32.8895,16.8755 C32.6495,16.5505 32.3545,16.328 32.0095,16.2055 C31.662,16.088 31.2745,16.0255 30.8395,16.0255 C30.407,16.0255 30.017,16.088 29.6695,16.213 C29.327,16.338 29.0295,16.5655 28.7845,16.8955 C28.537,17.2255 28.3495,17.673 28.222,18.2455 C28.092,18.813 28.027,19.5405 28.027,20.4255 C28.027,21.3255 28.092,22.0605 28.222,22.633 C28.3495,23.203 28.537,23.6505 28.7845,23.9755 C29.0295,24.3005 29.327,24.5255 29.6695,24.643" id="Fill-5" fill="#131313"></path>
                                <path d="M36.27675,16.375 C36.27675,15.9675 36.48425,15.7625 36.90175,15.7625 C37.31925,15.7625 37.52675,15.9675 37.52675,16.375 C37.52675,16.7925 37.31925,17 36.90175,17 C36.48425,17 36.27675,16.7925 36.27675,16.375 M37.86425,17.2625 C38.07425,17.0475 38.17675,16.74 38.17675,16.3375 C38.17675,16.0125 38.07425,15.7375 37.86425,15.5125 C37.65675,15.2875 37.33925,15.1775 36.91425,15.1775 C36.47175,15.1775 36.14675,15.2825 35.93925,15.5 C35.72925,15.7175 35.62675,16.0025 35.62675,16.35 C35.62675,16.7275 35.72925,17.025 35.93925,17.2525 C36.14675,17.475 36.46425,17.5875 36.88925,17.5875 C37.33175,17.5875 37.65675,17.48 37.86425,17.2625" id="Fill-7" fill="#131313"></path>
                                <path d="M13.701,33.36725 C15.6285,33.97225 17.716,34.34975 19.871,34.61225 L16.616,37.86725 C16.1285,38.35475 16.1285,39.14725 16.616,39.63475 C16.861,39.87725 17.181,39.99975 17.501,39.99975 C17.8185,39.99975 18.1385,39.87725 18.3835,39.63475 L23.3835,34.63475 C23.871,34.14725 23.871,33.35475 23.3835,32.86725 L18.3835,27.86725 C17.896,27.37725 17.1035,27.37725 16.616,27.86725 C16.1285,28.35475 16.1285,29.14725 16.616,29.63475 L18.9785,31.99475 C17.401,31.74975 15.8685,31.42975 14.4285,30.97725 C7.071,28.80475 2.501,24.59725 2.501,19.99975 C2.501,14.09975 10.3685,8.82975 20.806,7.74225 C21.491,7.67225 21.991,7.05725 21.9185,6.36975 C21.8485,5.68225 21.226,5.17475 20.546,5.25725 C8.641,6.49475 0.001,12.69475 0.001,19.99975 C0.001,25.75225 5.256,30.87725 13.701,33.36725" id="Fill-9" fill="#131313"></path>
                                <g id="Group-13" transform="translate(25.000000, 0.000000)">
                                    <mask id="mask-2" fill="white">
                                        <use xlinkHref="#path-1"></use>
                                    </mask>
                                    <g id="Clip-12"></g>
                                    <path d="M6.61625,12.13425 C6.86125,12.37925 7.18125,12.49925 7.50125,12.49925 C7.81875,12.49925 8.13875,12.37925 8.38375,12.13425 C8.87125,11.64675 8.87125,10.85425 8.38375,10.36675 L6.02125,8.00425 C7.59875,8.24925 9.13125,8.57175 10.57125,9.02425 C17.92875,11.19675 22.50125,15.40425 22.50125,19.99925 C22.50125,25.90425 14.63125,31.17175 4.19625,32.25925 C3.51125,32.32925 3.01125,32.94425 3.08375,33.63175 C3.14875,34.27175 3.69125,34.75175 4.32375,34.75175 C4.36875,34.75175 4.41125,34.74925 4.45625,34.74425 C16.35875,33.50675 25.00125,27.30425 25.00125,19.99925 C25.00125,14.24925 19.74375,9.12425 11.29875,6.63425 C9.37625,6.02925 7.28625,5.65175 5.12875,5.38925 L8.38375,2.13425 C8.87125,1.64675 8.87125,0.85425 8.38375,0.36675 C7.89625,-0.12075 7.10375,-0.12075 6.61625,0.36675 L1.61625,5.36675 C1.12875,5.85425 1.12875,6.64675 1.61625,7.13425 L6.61625,12.13425 Z" id="Fill-11" fill="#131313" mask="url(#mask-2)"></path>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
            </g>
        </g>
    </svg>

    if (this.props.icon === true) {
      return (
        <div ref={this.canvasContainer} className="flex flex-col items-center">
          {this.props.iconPosition === "top" && <div className="mb-12">
            {icon}
          </div>}
          <canvas
            ref={this.canvas}
            className={`cursor-pointer active:cursor-move transition transition-opacity duration-500 ${
              this.props.loadedCanvas ? 'opacity-1' : 'opacity-0'
            }`}
          />
          {this.props.iconPosition === "bottom" && <div className="mt-12">
            {icon}
          </div>}
        </div>
      )
    }
    
    return (
      <canvas
        ref={this.canvas}
        className={`cursor-pointer active:cursor-move transition transition-opacity duration-500 ${
          this.props.loadedCanvas ? 'opacity-1' : 'opacity-0'
        }`}
      />
    )
  }
}

export default Viewer
